import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PurchaseModal from "@/pages/marketplace/PurchaseModal";
import { Users as UsersIcon, MagnifyingGlass, ShoppingBag, CreditCard, Storefront, CaretDown, CaretUp } from "@phosphor-icons/react";

/**
 * Marketplace admin/supervisor — prenotazione servizio "per conto di" un pro.
 * Flusso: selezione pro → visualizzazione fascia sconto + plafond → catalogo
 * servizi con prezzo fascia → PurchaseModal (payload contiene `on_behalf_of_professional_id`).
 * La pratica risultante viene registrata come proprietà del pro; il caller
 * (admin/supervisor) è tracciato in `booked_by_admin_id` + history.
 */
// Iter209e9 — Ragione sociale come nome principale del pro (fallback nome e cognome).
const proDisplayName = (p) =>
  (p.company_name || "").trim() || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email;

export default function AdminMarketplace() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAllowed = user?.role === "admin" || user?.is_supervisor;

  const [pros, setPros] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedPro, setSelectedPro] = useState(null);
  const [previewRefresh, setPreviewRefresh] = useState(0);
  const [proProperties, setProProperties] = useState([]);
  const [previewMap, setPreviewMap] = useState({}); // serviceId → preview data
  const [query, setQuery] = useState("");
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter140i: espansione collaboratori per singolo Master + cache lazy dei sub.
  const [expandedMasters, setExpandedMasters] = useState(() => new Set());
  const [subsByMaster, setSubsByMaster] = useState({}); // masterId → [subUsers]
  const [loadingSubs, setLoadingSubs] = useState({}); // masterId → bool

  useEffect(() => {
    if (!isAllowed) { nav("/dashboard"); return; }
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [proResp, svcResp] = await Promise.all([
        // Iter140i: `masters_only=true` → il server invia solo i Master Pro.
        // I collaboratori vengono richiesti on-demand quando l'Admin apre
        // la tendina della card.
        api.get("/users?role=professional&status=approved&masters_only=true"),
        api.get("/services"),
      ]);
      const normalized = (proResp.data || []).map(p => ({ ...p, _id: p._id || p.id }));
      setPros(normalized);
      setServices((svcResp.data || []).filter(s => (s.status || "active") === "active" && s.available_in_marketplace !== false));
    } catch (err) {
      toast.error("Errore caricamento dati");
    } finally { setLoading(false); }
  };

  // Iter140i: carica lazy i sub-utenti di un master alla prima espansione.
  const loadSubsFor = async (masterId) => {
    if (subsByMaster[masterId] !== undefined) return; // già caricati
    setLoadingSubs(prev => ({ ...prev, [masterId]: true }));
    try {
      const r = await api.get(`/users?role=professional&status=approved&parent_user_id=${masterId}`);
      const normalized = (r.data || []).map(p => ({ ...p, _id: p._id || p.id }));
      setSubsByMaster(prev => ({ ...prev, [masterId]: normalized }));
    } catch {
      setSubsByMaster(prev => ({ ...prev, [masterId]: [] }));
      toast.error("Errore caricamento collaboratori");
    } finally {
      setLoadingSubs(prev => ({ ...prev, [masterId]: false }));
    }
  };

  const toggleMasterExpansion = (masterId) => {
    setExpandedMasters(prev => {
      const next = new Set(prev);
      if (next.has(masterId)) {
        next.delete(masterId);
      } else {
        next.add(masterId);
        // Trigger caricamento lazy (fire-and-forget).
        loadSubsFor(masterId);
      }
      return next;
    });
  };

  // Quando si seleziona un pro: preload immobili + preview prezzo per tutti i servizi
  useEffect(() => {
    if (!selectedPro || !services.length) { setPreviewMap({}); setProProperties([]); return; }
    (async () => {
      try {
        const proResp = await api.get(`/properties?owner_id=${selectedPro._id}`);
        setProProperties(proResp.data || []);
      } catch { setProProperties([]); }
      const next = {};
      await Promise.all(services.map(async (s) => {
        try {
          const r = await api.get(`/admin/purchase-preview?service_id=${s._id}&professional_id=${selectedPro._id}`);
          next[s._id] = r.data;
        } catch (err) { /* ignore per-service failures */ }
      }));
      setPreviewMap(next);
    })();
  }, [selectedPro, services, previewRefresh]);

  const filteredPros = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pros;
    return pros.filter(p => {
      const full = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
      return full.includes(q) || (p.email || "").toLowerCase().includes(q) || (p.company_name || "").toLowerCase().includes(q);
    });
  }, [pros, query]);

  const proSummary = useMemo(() => {
    if (!selectedPro) return null;
    // Ricavo fascia/plafond dal primo preview disponibile (tutti hanno gli stessi metadata pro)
    const anyPreview = Object.values(previewMap)[0];
    return {
      fascia: anyPreview?.fascia,
      plafond: anyPreview?.plafond,
    };
  }, [selectedPro, previewMap]);

  const fmt = (n) => n == null ? "—" : `€ ${Number(n).toFixed(2).replace(".", ",")}`;

  if (!isAllowed) return null;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 space-y-6" data-testid="admin-marketplace-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700 mb-1">Marketplace PRO</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Storefront size={28} weight="duotone" color="#6D28D9" />
            Prenota un servizio per conto di un professionista
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Usa questa sezione quando un pro ti chiama al telefono e ha bisogno di aiuto per acquistare un servizio.
          </p>
        </div>
        {selectedPro && (
          <Button variant="outline" onClick={() => { setSelectedPro(null); setSelectedSvc(null); }} data-testid="reset-pro-btn">
            Cambia professionista
          </Button>
        )}
      </div>

      {/* STEP 1 — Selezione professionista */}
      {!selectedPro && (
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <UsersIcon size={20} color="#6D28D9" weight="duotone" />
            <h2 className="font-display text-lg font-semibold">1. Seleziona il professionista</h2>
          </div>
          <div className="relative mb-3">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, email, azienda…"
              className="pl-9"
              data-testid="pro-search-input"
            />
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Caricamento…</div>
          ) : filteredPros.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Nessun professionista trovato</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredPros.map(p => {
                const expanded = expandedMasters.has(p._id);
                const subs = subsByMaster[p._id];
                const isLoadingSub = !!loadingSubs[p._id];
                return (
                  <div
                    key={p._id}
                    className="p-3 border rounded-lg bg-white hover:border-purple-400 transition"
                    data-testid={`master-card-${p._id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedPro(p)}
                      data-testid={`pick-pro-${p._id}`}
                      className="w-full text-left"
                    >
                      <div className="font-semibold text-sm text-slate-900 truncate">
                        {proDisplayName(p)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                    </button>
                    {/* Iter140i: sezione collaboratori (lazy-load on expand) */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleMasterExpansion(p._id); }}
                      className="mt-2 w-full flex items-center justify-between text-[11px] text-purple-700 hover:text-purple-900 border-t pt-2"
                      data-testid={`master-toggle-subs-${p._id}`}
                    >
                      <span className="uppercase tracking-wider font-semibold">
                        Collaboratori
                        {subs !== undefined && subs.length > 0 && (
                          <span className="ml-1 text-slate-500 font-normal">({subs.length})</span>
                        )}
                      </span>
                      {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    </button>
                    {expanded && (
                      <div className="mt-1 space-y-1" data-testid={`master-subs-${p._id}`}>
                        {isLoadingSub && (
                          <div className="text-[11px] text-slate-500 italic py-1">Caricamento…</div>
                        )}
                        {!isLoadingSub && subs !== undefined && subs.length === 0 && (
                          <div className="text-[11px] text-slate-500 italic py-1">Nessun collaboratore</div>
                        )}
                        {!isLoadingSub && subs !== undefined && subs.map(sub => (
                          <button
                            key={sub._id}
                            type="button"
                            onClick={() => setSelectedPro(sub)}
                            className="w-full text-left px-2 py-1.5 rounded bg-purple-50 border border-purple-100 hover:bg-purple-100 text-[12px] flex items-center gap-2"
                            data-testid={`pick-sub-${sub._id}`}
                          >
                            <span className="text-purple-400" aria-hidden>↳</span>
                            <span className="font-medium text-slate-800 truncate">
                              {`${sub.first_name || ""} ${sub.last_name || ""}`.trim() || sub.email}
                            </span>
                            <span className="text-slate-500 truncate ml-auto">{sub.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* STEP 2 — Pro summary + Catalogo servizi */}
      {selectedPro && (
        <>
          <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-5 shadow-sm" data-testid="pro-summary-panel">
            <div className="flex items-center gap-2 mb-3">
              <UsersIcon size={18} color="#6D28D9" weight="duotone" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700">Professionista selezionato</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="text-sm text-slate-500">Nome</div>
                <div className="font-semibold text-slate-900" data-testid="selected-pro-name">
                  {proDisplayName(selectedPro)}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{selectedPro.email}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Fascia sconto</div>
                <div className="font-semibold text-purple-800" data-testid="selected-pro-fascia">
                  {proSummary?.fascia ? `${proSummary.fascia.name} (−${proSummary.fascia.discount_percent}%)` : "Nessuna (Standard)"}
                </div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Plafond mensile</div>
                <div className="font-mono text-sm" data-testid="selected-pro-plafond">
                  {proSummary?.plafond
                    ? `${fmt(proSummary.plafond.used_current_month)} / ${fmt(proSummary.plafond.monthly_limit)}`
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag size={20} color="#6D28D9" weight="duotone" />
              <h2 className="font-display text-lg font-semibold">2. Scegli il servizio</h2>
            </div>
            {services.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Nessun servizio disponibile</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {services.map(s => {
                  const preview = previewMap[s._id];
                  const overspend = preview?.plafond?.overspend;
                  return (
                    <div
                      key={s._id}
                      className="border rounded-lg p-4 bg-white hover:shadow-md transition flex flex-col"
                      data-testid={`admin-svc-card-${s._id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="font-semibold text-slate-900 truncate flex-1">{s.name}</div>
                        {s.fixed_price && (
                          <span className="text-[9px] font-mono uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 shrink-0" title="Prezzo uguale per tutti i pro">
                            💎 Fisso
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[32px]">
                        {s.description || "—"}
                      </div>
                      <div className="mt-auto space-y-1.5 border-t pt-3">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Prezzo base</span>
                          <span className="font-mono line-through text-slate-400">{fmt(preview?.service?.base_price ?? s.base_price)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700 font-medium">Prezzo per il pro</span>
                          <span className="font-mono font-bold text-purple-800" data-testid={`admin-price-${s._id}`}>
                            {fmt(preview?.professional_price)}
                          </span>
                        </div>
                        {overspend && (
                          <div className="text-[10px] text-red-700 font-semibold" data-testid={`admin-overspend-${s._id}`}>
                            ⚠️ Sfora il plafond mensile
                          </div>
                        )}
                        <Button
                          className="w-full ht-gradient border-0 text-white mt-2"
                          onClick={() => {
                            const preview = previewMap[s._id];
                            setSelectedSvc(
                              preview?.professional_price != null
                                ? { ...s, professional_price: preview.professional_price, fascia_discount_pct: Number(preview?.fascia?.discount_percent) || 0 }
                                : s,
                            );
                          }}
                          data-testid={`admin-book-${s._id}`}
                        >
                          <CreditCard size={14} className="mr-2" /> Prenota
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* PurchaseModal riusato: passiamo il pro target come `user` synthetic e
          `onBehalfOfProId` per il backend. Le proprietà sono quelle del pro. */}
      {selectedPro && (
        <PurchaseModal
          selected={selectedSvc}
          services={services}
          properties={proProperties}
          user={{
            id: selectedPro._id,
            _id: selectedPro._id,
            role: "professional",
            email: selectedPro.email,
            first_name: selectedPro.first_name,
            last_name: selectedPro.last_name,
            fascia_id: selectedPro.fascia_id,
            parent_user_id: selectedPro.parent_user_id,
            plafond_monthly_limit: proSummary?.plafond?.monthly_limit,
            plafond_used_current_month: proSummary?.plafond?.used_current_month,
          }}
          plafond={proSummary?.plafond ? {
            used_current_month: proSummary.plafond.used_current_month,
            monthly_limit: proSummary.plafond.monthly_limit,
          } : null}
          onBehalfOfProId={selectedPro._id}
          onClose={() => { setSelectedSvc(null); setPreviewRefresh((k) => k + 1); }}
          onPropertiesChanged={async () => {
            try { const r = await api.get(`/properties?owner_id=${selectedPro._id}`); setProProperties(r.data || []); } catch {}
          }}
          onQuotesChanged={() => {}}
        />
      )}
    </div>
  );
}
